import { useEffect, useMemo, useState } from 'react';
import { Input, Space, Tooltip } from 'antd';
import debounce from 'lodash/debounce';
import { isAmountBound } from '@/domain/filters';

interface Props {
  onChange: (min?: string, max?: string) => void;
  /** 外部「清空筛选」之后要把本地输入也抹掉；值变了就重置。 */
  resetKey: number;
}

/**
 * 金额区间筛选。
 *
 * 🔴🔴 **这里的两个值全程是字符串，不许出现 InputNumber，也不许出现 Number()。**
 * antd 的 InputNumber 交出来的是 JavaScript number —— 1234567.89 经它一手就可能变成
 * 1234567.8899999999，而这个边界会被拿去和数据库里的 numeric 比较，
 * 悄悄把一笔本该被看到的打款筛掉。用最朴素的 Input，从头到尾不碰数值类型。
 *
 * 格式不合法时**不往上抛**，只标红：抛上去的结果是每敲一个字符打一次必然 400 的请求。
 */
export default function AmountRangeFilter({ onChange, resetKey }: Props) {
  const [min, setMin] = useState('');
  const [max, setMax] = useState('');

  const minOk = min === '' || isAmountBound(min);
  const maxOk = max === '' || isAmountBound(max);

  const push = useMemo(
    () =>
      debounce((lo: string, hi: string) => {
        const loOk = lo === '' || isAmountBound(lo);
        const hiOk = hi === '' || isAmountBound(hi);
        if (!loOk || !hiOk) return;
        onChange(lo || undefined, hi || undefined);
      }, 400),
    [onChange],
  );

  useEffect(() => () => push.cancel(), [push]);

  useEffect(() => {
    if (resetKey === 0) return;
    push.cancel();
    // oxlint-disable-next-line react/set-state-in-effect
    setMin('');
    setMax('');
  }, [resetKey, push]);

  return (
    <Space.Compact>
      <Tooltip title={minOk ? '最小金额（含）' : '最多两位小数的非负数'}>
        <Input
          style={{ width: 110 }}
          placeholder="金额 ≥"
          inputMode="decimal"
          status={minOk ? undefined : 'error'}
          value={min}
          onChange={(e) => {
            setMin(e.target.value);
            push(e.target.value, max);
          }}
        />
      </Tooltip>
      <Tooltip title={maxOk ? '最大金额（含）' : '最多两位小数的非负数'}>
        <Input
          style={{ width: 110 }}
          placeholder="金额 ≤"
          inputMode="decimal"
          status={maxOk ? undefined : 'error'}
          value={max}
          onChange={(e) => {
            setMax(e.target.value);
            push(min, e.target.value);
          }}
        />
      </Tooltip>
    </Space.Compact>
  );
}
